import { z } from 'zod';
import { estDatePure, jourDansFuseau } from './jour.js';
import { estChampDeTrace } from './base.js';

export type Condition =
  | { et: Condition[] }
  | { ou: Condition[] }
  | { champ: string; op: 'avantAujourdhui' }
  | { champ: string; op: 'vide'; type?: 'texte' }
  | { champ: string; op: 'hors'; valeurs: (string | number | boolean)[] }
  | { champ: string; op: 'parmi'; valeurs: (string | number | boolean)[] }
  | { champ: string; op: 'moi' };

const champ = z.string().regex(/^(?:socle:)?[a-z][a-zA-Z0-9_]*$/);
// Une borne a la lecture, avant de laisser Zod descendre dans une declaration.
export function profondeurDeclaree(value: unknown, profondeur = 0): boolean {
  if (profondeur > 8) return false;
  if (!value || typeof value !== 'object') return true;
  return Object.values(value).every(child => profondeurDeclaree(child, profondeur + 1));
}
const arbre: z.ZodType<Condition> = z.lazy(() => z.union([
  z.object({ et: z.array(arbre).min(1).max(32) }).strict(),
  z.object({ ou: z.array(arbre).min(1).max(32) }).strict(),
  z.object({ champ, op: z.literal('avantAujourdhui') }).strict(),
  z.object({ champ, op: z.literal('vide'), type: z.literal('texte').optional() }).strict(),
  z.object({ champ, op: z.literal('hors'), valeurs: z.array(z.union([z.string(), z.number(), z.boolean()])).min(1).max(32) }).strict(),
  z.object({ champ, op: z.literal('parmi'), valeurs: z.array(z.union([z.string(), z.number(), z.boolean()])).min(1).max(32) }).strict(),
  z.object({ champ, op: z.literal('moi') }).strict(),
]));
export const conditionSchema = z.unknown().superRefine((value, ctx) => {
  if (!profondeurDeclaree(value)) ctx.addIssue({ code: 'custom', message: 'condition trop profonde' });
}).pipe(arbre);

export function champsDeCondition(condition: Condition): string[] {
  if ('et' in condition) return condition.et.flatMap(champsDeCondition);
  if ('ou' in condition) return condition.ou.flatMap(champsDeCondition);
  return [condition.champ];
}

/**
 * `moi` : la personne connectee. Un champ `personne` stocke un `personId`, une
 * trace (`socle:createdBy`) stocke un uid : le test « est moi » compare chacun
 * a sa clef. Absente ou inconnue, aucune fiche n'est a moi.
 */
export interface ContexteDeCondition {
  maintenant?: Date;
  timezone?: string;
  moi?: { personId: string | null; uid: string | null } | null;
}

/**
 * LES CHAMPS QU'UNE CONDITION VEUT « MOI » ET QU'ON PEUT ECRIRE : ses feuilles
 * `moi` hors traces (l'auteur se pose seul). Une fiche creee dans une porte
 * tranchee sur moi les recoit prerempli, sinon elle n'y apparaitrait pas.
 */
export function champsMoiAEcrire(condition: Condition): string[] {
  if ('et' in condition) return condition.et.flatMap(champsMoiAEcrire);
  if ('ou' in condition) return condition.ou.flatMap(champsMoiAEcrire);
  return condition.op === 'moi' && !estTraceDeCompte(condition.champ) ? [condition.champ] : [];
}

/**
 * LES VALEURS QU'UNE CONDITION FIXE : ses feuilles `parmi` a UNE valeur,
 * atteintes par des `et` seulement (sous un `ou`, rien n'est sur). Une fiche
 * creee dans la porte « Sales » (`pole` parmi `sales`) nait avec ce pole, sinon
 * elle quitterait la porte a l'enregistrement.
 */
export function valeursFixees(condition: Condition): [string, string | number | boolean][] {
  if ('et' in condition) return condition.et.flatMap(valeursFixees);
  if ('ou' in condition) return [];
  return condition.op === 'parmi' && condition.valeurs.length === 1 && !estTraceDeCompte(condition.champ)
    ? [[condition.champ, condition.valeurs[0]!]]
    : [];
}

/**
 * UNE VALEUR DE CE CHAMP PEUT-ELLE PASSER LA CONDITION ? Lu sur les feuilles
 * `parmi` et `hors` de ce champ atteintes par des `et` ; tout le reste (un `ou`,
 * un autre champ, un autre operateur) ne dit rien et laisse passer. C'est ce
 * qui borne les colonnes d'un kanban a ce que sa porte peut montrer.
 */
export function valeurPermise(condition: Condition, champ: string, valeur: string | number | boolean): boolean {
  if ('et' in condition) return condition.et.every(c => valeurPermise(c, champ, valeur));
  if ('ou' in condition || condition.champ !== champ) return true;
  if (condition.op === 'parmi') return condition.valeurs.includes(valeur);
  if (condition.op === 'hors') return !condition.valeurs.includes(valeur);
  return true;
}

/** Le champ est-il une trace du socle (un uid), plutot qu'une personne (un `personId`) ? */
export function estTraceDeCompte(champ: string): boolean {
  return estChampDeTrace(champ.replace(/^socle:/, ''));
}
export function evaluerCondition(condition: Condition, lire: (champ: string) => unknown, contexte: ContexteDeCondition = {}): boolean {
  if ('et' in condition) return condition.et.every(c => evaluerCondition(c, lire, contexte));
  if ('ou' in condition) return condition.ou.some(c => evaluerCondition(c, lire, contexte));
  const value = lire(condition.champ);
  if (condition.op === 'hors') return !condition.valeurs.some(v => v === value);
  if (condition.op === 'parmi') return Array.isArray(value) ? value.some(v => condition.valeurs.includes(v)) : condition.valeurs.some(v => v === value);
  if (condition.op === 'moi') {
    const clef = estTraceDeCompte(condition.champ) ? contexte.moi?.uid : contexte.moi?.personId;
    if (!clef) return false;
    return Array.isArray(value) ? value.includes(clef) : value === clef;
  }
  if (condition.op === 'vide') return value == null || (typeof value === 'string' && value.trim() === '')
    || (Array.isArray(value) && value.length === 0) || (condition.type === 'texte' && typeof value !== 'string');
  let jour: string;
  if (estDatePure(value)) jour = value;
  else {
    const instant = value instanceof Date ? value : typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value) ? new Date(value) : null;
    if (!instant || !Number.isFinite(instant.getTime())) return false;
    jour = jourDansFuseau(instant, contexte.timezone);
  }
  return jour < jourDansFuseau(contexte.maintenant ?? new Date(), contexte.timezone);
}
